import type { CopilotContractKey } from './copilot.ts';
import type { ToolContinuationCapability } from './continuations.ts';
import type {
  PrebuiltToolKey,
  PrebuiltToolPackKey,
  ToolPageMode,
} from './prebuilt-tools.ts';
import type {
  SkillKey,
  SkillReadScope,
} from './skills.ts';
import type { ViewPatternKey } from './views.ts';

export type ToolCategory =
  | 'sales'
  | 'crm'
  | 'email'
  | 'meeting'
  | 'reporting';

export type ToolIconKey =
  | 'bar_chart_3'
  | 'building_2'
  | 'calendar_clock'
  | 'mail'
  | 'phone_call';

export type ToolLauncherVariant =
  | 'manual_form'
  | 'textarea';

export type ToolFieldType =
  | 'email'
  | 'select'
  | 'text'
  | 'textarea';

export type ToolSectionId =
  | 'actions'
  | 'artifact'
  | 'context'
  | 'detail'
  | 'summary';

export interface ToolFieldOption {
  value: string;
  label: string;
}

export interface ToolFieldDefinition {
  key: string;
  label: string;
  type: ToolFieldType;
  required: boolean;
  placeholder?: string;
  rows?: number;
  options?: ToolFieldOption[];
  description?: string;
}

export interface ToolProductSpec {
  title: string;
  promise: string;
  description: string;
  category: ToolCategory;
  pageMode: ToolPageMode;
  primaryJob: string;
}

export interface ToolLaunchSpec {
  variant: ToolLauncherVariant;
  icon: ToolIconKey;
  launchLabel: string;
  composerTitle: string;
  composerDescription: string;
  submitLabel: string;
  fields: ToolFieldDefinition[];
  defaultValues: Record<string, string>;
}

export interface ToolExecutionSpec {
  skillKey: SkillKey;
  flowTemplateKey: string;
  packKey: PrebuiltToolPackKey;
  packVersion: string;
  workspaceKind: 'shared';
  defaultReadScope: SkillReadScope;
}

export interface ToolOutputSpec {
  pageMode: ToolPageMode;
  expectedSections: ToolSectionId[];
  allowedPatterns: ViewPatternKey[];
  sectionOrder?: ToolSectionId[];
}

export interface ToolCopilotSpec {
  contractKey: CopilotContractKey;
}

export interface ToolManifest {
  key: PrebuiltToolKey;
  version: string;
  product: ToolProductSpec;
  launch: ToolLaunchSpec;
  execution: ToolExecutionSpec;
  output: ToolOutputSpec;
  copilot: ToolCopilotSpec;
  continuity?: {
    inputCapability: ToolContinuationCapability;
  };
}

export const PREBUILT_TOOL_MANIFESTS = {
  analyze_call: {
    key: 'analyze_call',
    version: '1.0.0',
    product: {
      title: 'Analyser un appel',
      promise: 'Transformez une transcription ou des notes d’appel en prochaines actions claires et en contexte utile.',
      description: 'Utilisez cette application pour obtenir une lecture nette de l’appel, un résultat stable et une conversation qui conserve son contexte.',
      category: 'sales',
      pageMode: 'diagnostic',
      primaryJob: 'Transformer un appel commercial en prochaines actions claires et en contexte utile.',
    },
    launch: {
      variant: 'textarea',
      icon: 'phone_call',
      launchLabel: 'Analyser un appel',
      composerTitle: 'Préparer l’analyse',
      composerDescription: 'Collez une transcription, des notes d’appel ou un résumé. Un contenu partiel reste utile tant qu’il décrit fidèlement la situation.',
      submitLabel: 'Analyser l’appel',
      fields: [
        {
          key: 'transcript',
          label: 'Transcription ou notes d’appel',
          type: 'textarea',
          required: true,
          rows: 8,
          placeholder: 'Collez la transcription, les notes de l’appel ou un résumé, même partiel.',
          description: 'Plus le texte contient des noms, objections, engagements ou échéances, plus le résultat sera précis.',
        },
      ],
      defaultValues: {
        transcript: '',
      },
    },
    execution: {
      skillKey: 'analyze_call',
      flowTemplateKey: 'post_call_actions',
      packKey: 'domino_sales_core',
      packVersion: '1.0.0',
      workspaceKind: 'shared',
      defaultReadScope: 'actor',
    },
    output: {
      pageMode: 'diagnostic',
      expectedSections: ['summary', 'actions', 'context'],
      allowedPatterns: ['score_card', 'action_list', 'entity_summary', 'fallback_empty'],
    },
    copilot: {
      contractKey: 'analyze_call',
    },
  },
  draft_followup: {
    key: 'draft_followup',
    version: '1.0.0',
    product: {
      title: 'Préparer le suivi',
      promise: 'Préparez un email de suivi prêt à envoyer à partir d’un appel, avec le bon ton et une suite claire.',
      description: 'Utilisez cette application pour transformer un appel ou des notes en email de suivi concret et prêt à relire.',
      category: 'email',
      pageMode: 'artifact',
      primaryJob: 'Produire un e-mail de suivi prêt à relire, ajuster et envoyer.',
    },
    launch: {
      variant: 'manual_form',
      icon: 'mail',
      launchLabel: 'Préparer un suivi',
      composerTitle: 'Préparer le suivi',
      composerDescription: 'Complétez le contexte utile puis laissez Domino rédiger un brouillon prêt à relire. Un travail précédent peut déjà avoir renseigné les champs essentiels.',
      submitLabel: 'Rédiger le suivi',
      fields: [
        {
          key: 'call_summary',
          label: 'Résumé de l’appel ou notes',
          type: 'textarea',
          required: true,
          rows: 7,
          placeholder: 'Résumé de l’appel, points clés, objections, engagements, prochaines étapes…',
        },
        {
          key: 'recipient_name',
          label: 'Nom du destinataire',
          type: 'text',
          required: true,
          placeholder: 'Ex: Marie Dupont',
        },
        {
          key: 'recipient_email',
          label: 'Email du destinataire',
          type: 'email',
          required: false,
          placeholder: 'Ex: marie@entreprise.com',
        },
        {
          key: 'tone',
          label: 'Ton',
          type: 'select',
          required: false,
          options: [
            { value: 'friendly', label: 'Chaleureux' },
            { value: 'formal', label: 'Formel' },
            { value: 'direct', label: 'Direct' },
          ],
        },
        {
          key: 'objective',
          label: 'Intention commerciale',
          type: 'select',
          required: false,
          options: [
            { value: 'recap_and_next_steps', label: 'Récapitulatif et prochaines étapes' },
            { value: 'send_resource', label: 'Envoyer une ressource' },
            { value: 'schedule_meeting', label: 'Proposer un rendez-vous' },
            { value: 'close_action', label: 'Clore proprement' },
          ],
        },
        {
          key: 'additional_context',
          label: 'Contexte additionnel',
          type: 'textarea',
          required: false,
          rows: 5,
          placeholder: 'Tout autre élément utile à reprendre ou à nuancer dans l email.',
        },
        {
          key: 'language',
          label: 'Langue',
          type: 'select',
          required: false,
          options: [
            { value: 'fr', label: 'Français' },
            { value: 'en', label: 'English' },
          ],
        },
      ],
      defaultValues: {
        call_summary: '',
        recipient_name: '',
        recipient_email: '',
        tone: 'friendly',
        objective: 'recap_and_next_steps',
        additional_context: '',
        language: 'fr',
      },
    },
    execution: {
      skillKey: 'draft_followup',
      flowTemplateKey: 'draft_followup',
      packKey: 'domino_sales_core',
      packVersion: '1.0.0',
      workspaceKind: 'shared',
      defaultReadScope: 'actor',
    },
    output: {
      pageMode: 'artifact',
      expectedSections: ['artifact', 'actions', 'context'],
      allowedPatterns: ['artifact_document', 'action_list', 'entity_summary', 'fallback_empty'],
    },
    copilot: {
      contractKey: 'draft_followup',
    },
    continuity: {
      inputCapability: {
        label: 'Préparer le suivi',
        description: 'Rédiger l email de suivi à partir de cette analyse',
        jobKey: 'followup_email',
        acceptsCategories: ['email', 'resource'],
        situationAffinity: 'momentum',
        priority: 10,
        prefillMapping: {
          call_summary: '{{derived.followupSummary}}',
          recipient_name: '{{primaryContactName}}',
          recipient_email: '{{primaryContactEmail}}',
          objective: '{{derived.followupObjective}}',
          tone: '{{derived.followupTone}}',
          language: 'fr',
          additional_context: '{{derived.companyContext}}',
        },
      },
    },
  },
  prepare_meeting: {
    key: 'prepare_meeting',
    version: '2.0.0',
    product: {
      title: 'Préparer le rendez-vous',
      promise: 'Domino recherche le contexte utile puis prépare un brief concret avant votre rendez-vous.',
      description: 'Utilisez cette application avant un rendez-vous important pour réunir le contexte web, CRM, calendrier et historique utile à la synthèse finale.',
      category: 'meeting',
      pageMode: 'artifact',
      primaryJob: 'Produire un brief de rendez-vous concret, ancre dans les donnees disponibles.',
    },
    launch: {
      variant: 'manual_form',
      icon: 'calendar_clock',
      launchLabel: 'Préparer le rendez-vous',
      composerTitle: 'Préparer le rendez-vous',
      composerDescription: 'Renseignez le minimum utile sur le rendez-vous. Le contexte additionnel reste optionnel : Domino doit faire le gros du travail de recherche lui-même.',
      submitLabel: 'Construire le brief',
      fields: [
        {
          key: 'company_name',
          label: 'Compte ou entreprise',
          type: 'text',
          required: true,
          placeholder: 'Ex: Innovorder',
        },
        {
          key: 'contact_name',
          label: 'Contact principal',
          type: 'text',
          required: false,
          placeholder: 'Ex: Romain Martin',
        },
        {
          key: 'contact_email',
          label: 'Email du contact',
          type: 'email',
          required: false,
          placeholder: 'Ex: romain@entreprise.com',
        },
        {
          key: 'meeting_title',
          label: 'Titre du rendez-vous',
          type: 'text',
          required: false,
          placeholder: 'Ex: Demo de cadrage',
        },
        {
          key: 'meeting_timing',
          label: 'Date ou période',
          type: 'text',
          required: false,
          placeholder: 'Ex: jeudi prochain, demain 14h',
        },
        {
          key: 'prep_goal',
          label: 'Objectif de préparation',
          type: 'select',
          required: false,
          options: [
            { value: 'advance_deal', label: 'Faire avancer l’opportunité' },
            { value: 'discovery', label: 'Qualifier un premier contact' },
            { value: 'risk_review', label: 'Revoir une opportunité à risque' },
            { value: 'demo_prep', label: 'Préparer une démo' },
            { value: 'negotiation', label: 'Préparer une négociation' },
            { value: 'relationship', label: 'Suivi de relation' },
            { value: 'other', label: 'Autre' },
          ],
        },
        {
          key: 'meeting_context',
          label: 'Contexte additionnel',
          type: 'textarea',
          required: false,
          rows: 5,
          placeholder: 'Ex: angle politique, point précis à tester, note interne, sujet sensible...',
        },
        {
          key: 'language',
          label: 'Langue',
          type: 'select',
          required: false,
          options: [
            { value: 'fr', label: 'Français' },
            { value: 'en', label: 'English' },
          ],
        },
      ],
      defaultValues: {
        company_name: '',
        contact_name: '',
        contact_email: '',
        meeting_timing: '',
        prep_goal: 'advance_deal',
        meeting_title: '',
        meeting_context: '',
        language: 'fr',
      },
    },
    execution: {
      skillKey: 'prepare_meeting',
      flowTemplateKey: 'prepare_meeting',
      packKey: 'domino_sales_core',
      packVersion: '2.0.0',
      workspaceKind: 'shared',
      defaultReadScope: 'actor',
    },
    output: {
      pageMode: 'artifact',
      expectedSections: ['artifact', 'actions', 'context'],
      allowedPatterns: ['artifact_document', 'action_list', 'entity_summary', 'fallback_empty'],
    },
    copilot: {
      contractKey: 'prepare_meeting',
    },
    continuity: {
      inputCapability: {
        label: 'Préparer le rendez-vous',
        description: 'Préparer un rendez-vous avec le contexte réuni par Domino',
        jobKey: 'meeting_prep',
        acceptsCategories: ['meeting'],
        situationAffinity: 'momentum',
        priority: 10,
        prefillMapping: {
          company_name: '{{company}}',
          contact_name: '{{primaryContactName}}',
          contact_email: '{{primaryContactEmail}}',
          meeting_timing: '{{action.deadline}}',
          prep_goal: 'advance_deal',
          meeting_context: '{{derived.followupSummary}}',
          language: 'fr',
        },
      },
    },
  },
  pipeline_health_check: {
    key: 'pipeline_health_check',
    version: '1.0.0',
    product: {
      title: 'Diagnostic du pipeline',
      promise: 'Domino lit votre pipeline CRM, croise l’activité récente, puis remonte les opportunités à traiter et les tendances de risque utiles.',
      description: 'Utilisez cette application pour repérer les opportunités à risque, les dates de clôture dépassées, les données CRM à corriger et les dossiers sans suivi récent.',
      category: 'reporting',
      pageMode: 'diagnostic',
      primaryJob: 'Diagnostiquer la santé du pipeline et prioriser les opportunités à traiter.',
    },
    launch: {
      variant: 'manual_form',
      icon: 'bar_chart_3',
      launchLabel: 'Analyser le pipeline',
      composerTitle: 'Analyser le pipeline',
      composerDescription: 'Choisissez le périmètre, l’horizon et les situations à examiner. HubSpot est requis pour produire une revue fiable.',
      submitLabel: 'Lancer la revue',
      fields: [
        {
          key: 'scope',
          label: 'Périmètre',
          type: 'select',
          required: false,
          options: [
            { value: 'my_pipeline', label: 'Mon pipeline' },
            { value: 'team_pipeline', label: 'Pipeline de l’équipe' },
          ],
        },
        {
          key: 'time_horizon',
          label: 'Horizon',
          type: 'select',
          required: false,
          options: [
            { value: '30d', label: '30 jours' },
            { value: '60d', label: '60 jours' },
            { value: '90d', label: '90 jours' },
            { value: '180d', label: '180 jours' },
            { value: 'all_open', label: 'Toutes les opportunités ouvertes' },
          ],
        },
        {
          key: 'focus',
          label: 'Situations à examiner',
          type: 'select',
          required: false,
          options: [
            { value: 'all', label: 'Tout le pipeline' },
            { value: 'at_risk', label: 'Opportunités à risque' },
            { value: 'stagnant', label: 'Opportunités sans avancée' },
            { value: 'closing_soon', label: 'Clôture prochaine' },
          ],
        },
        {
          key: 'min_amount',
          label: 'Montant minimum',
          type: 'text',
          required: false,
          placeholder: 'Ex: 10000',
          description: 'Laissez vide pour analyser tout le pipeline actif.',
        },
        {
          key: 'language',
          label: 'Langue',
          type: 'select',
          required: false,
          options: [
            { value: 'fr', label: 'Français' },
            { value: 'en', label: 'English' },
          ],
        },
      ],
      defaultValues: {
        scope: 'my_pipeline',
        time_horizon: '90d',
        focus: 'all',
        min_amount: '',
        language: 'fr',
      },
    },
    execution: {
      skillKey: 'pipeline_health_check',
      flowTemplateKey: 'pipeline_health_check',
      packKey: 'domino_sales_core',
      packVersion: '1.0.0',
      workspaceKind: 'shared',
      defaultReadScope: 'actor',
    },
    output: {
      pageMode: 'diagnostic',
      expectedSections: ['summary', 'actions', 'detail', 'context'],
      allowedPatterns: ['health_gauge', 'action_list', 'metric_grid', 'data_table', 'entity_summary', 'fallback_empty'],
      sectionOrder: ['summary', 'actions', 'detail', 'context'],
    },
    copilot: {
      contractKey: 'pipeline_health_check',
    },
    continuity: {
      inputCapability: {
        label: 'Examiner la santé du pipeline',
        description: 'Relire les opportunités CRM à risque et les dossiers sans suivi récent',
        jobKey: 'pipeline_review',
        acceptsCategories: ['crm_update', 'pipeline'],
        situationAffinity: 'universal',
        priority: 9,
        prefillMapping: {},
      },
    },
  },
  qualify_account: {
    key: 'qualify_account',
    version: '1.0.0',
    product: {
      title: 'Qualifier un compte',
      promise: 'Décidez si une entreprise doit être contactée maintenant, avec les arguments, les réserves et les sources qui justifient la suite.',
      description: 'Utilisez cette application avant une prospection ou une priorisation de compte. Domino croise les informations web, CRM et l’historique du compte, puis explique la suite recommandée.',
      category: 'sales',
      pageMode: 'diagnostic',
      primaryJob: 'Qualifier un compte avec preuves web, CRM et historique Domino avant action commerciale.',
    },
    launch: {
      variant: 'manual_form',
      icon: 'building_2',
      launchLabel: 'Qualifier un compte',
      composerTitle: 'Qualifier un compte',
      composerDescription: 'Renseignez le compte et, si besoin, vos critères ICP ou go/no-go. Le rendu final garde les preuves séparées de la synthèse.',
      submitLabel: 'Qualifier le compte',
      fields: [
        {
          key: 'company_name',
          label: 'Entreprise',
          type: 'text',
          required: true,
          placeholder: 'Ex: SIRFULL',
        },
        {
          key: 'company_domain',
          label: 'Domaine',
          type: 'text',
          required: false,
          placeholder: 'Ex: sirfull.com',
        },
        {
          key: 'contact_name',
          label: 'Contact principal',
          type: 'text',
          required: false,
          placeholder: 'Ex: Marie Dupont',
        },
        {
          key: 'contact_email',
          label: 'Email du contact',
          type: 'email',
          required: false,
          placeholder: 'Ex: marie@entreprise.com',
        },
        {
          key: 'qualification_goal',
          label: 'Intention commerciale',
          type: 'select',
          required: false,
          options: [
            { value: 'outbound', label: 'Prospection' },
            { value: 'expansion', label: 'Expansion' },
            { value: 'renewal', label: 'Renouvellement' },
            { value: 'partner', label: 'Partenariat' },
            { value: 'other', label: 'Autre' },
          ],
        },
        {
          key: 'icp_notes',
          label: 'ICP, go / no-go, critères client',
          type: 'textarea',
          required: false,
          rows: 5,
          placeholder: 'Ex: cible industrie software B2B, 50-500 employés, no-go secteur public, signal fort si projet transformation...',
        },
        {
          key: 'language',
          label: 'Langue',
          type: 'select',
          required: false,
          options: [
            { value: 'fr', label: 'Français' },
            { value: 'en', label: 'English' },
          ],
        },
      ],
      defaultValues: {
        company_name: '',
        company_domain: '',
        contact_name: '',
        contact_email: '',
        qualification_goal: 'outbound',
        icp_notes: '',
        language: 'fr',
      },
    },
    execution: {
      skillKey: 'qualify_account',
      flowTemplateKey: 'qualify_account',
      packKey: 'domino_sales_core',
      packVersion: '1.0.0',
      workspaceKind: 'shared',
      defaultReadScope: 'actor',
    },
    output: {
      pageMode: 'diagnostic',
      expectedSections: ['summary', 'actions', 'context', 'detail'],
      allowedPatterns: ['score_card', 'action_list', 'entity_summary', 'data_table', 'fallback_empty'],
      sectionOrder: ['summary', 'actions', 'context', 'detail'],
    },
    copilot: {
      contractKey: 'qualify_account',
    },
    continuity: {
      inputCapability: {
        label: 'Qualifier ce compte',
        description: 'Verifier si le compte est a contacter avec preuves web, CRM et historique Domino',
        jobKey: 'account_qualification',
        acceptsCategories: ['crm_update', 'pipeline', 'resource'],
        situationAffinity: 'universal',
        priority: 8,
        prefillMapping: {
          company_name: '{{company}}',
          contact_name: '{{primaryContactName}}',
          contact_email: '{{primaryContactEmail}}',
          qualification_goal: 'outbound',
          language: 'fr',
        },
      },
    },
  },
} as const satisfies Record<PrebuiltToolKey, ToolManifest>;

export const getPrebuiltToolManifest = (
  toolKey: PrebuiltToolKey,
): ToolManifest => PREBUILT_TOOL_MANIFESTS[toolKey];
